import { AfterContentInit, AfterViewInit, ApplicationRef, ChangeDetectorRef, Component, ComponentRef, ElementRef, inject, Input, OnInit, ViewChild, ViewContainerRef, OnChanges, SimpleChanges } from '@angular/core';
import { environment } from '../../../../environments/environment';
import * as mapboxgl from 'mapbox-gl/dist/mapbox-gl';
// Import only the specific turf function used (point) instead of the entire
// `@turf/turf` meta-package, which bundles all of turf into the initial build.
import { point } from "@turf/helpers";
import { distance } from "@turf/distance";
import { MatSelect } from '@angular/material/select';
import { formatNumber } from '@angular/common';
import { FracScheduleModel, FracScheduleReviewModel } from '../model';
import { MarkerPopupComponent } from '../marker-popup/marker-popup.component';
import { StatusColorPipe } from '../../../pipes/status-color.pipe';
import { WeatherService, WeatherForecast } from '../../../../services/weather.service';
import { HttpService } from '../../../utility/http-service';
@Component({
  selector: 'schedules-map',
  standalone: false,

  templateUrl: './schedules-map.component.html',
  styleUrl: '../schedules.scss'
})
export class SchedulesMapComponent implements OnInit, OnChanges {
  constructor(private statusColorPipe: StatusColorPipe, private applicationRef: ApplicationRef, private weatherService: WeatherService, private http: HttpService) {
  }
  @Input() data: FracScheduleModel[] = [];
  @Input() summaryData: {
    operating: number,
    next7: number,
    next8_30: number,
    next31_60: number,
    next61_90: number,
    total: number
  } = {
      operating: 0,
      next7: 0,
      next8_30: 0,
      next31_60: 0,
      next61_90: 0,
      total: 0
    };

  @ViewChild(MatSelect) selectComponent?: MatSelect;
  selectedMeasureUnits?: string;
  showMeasureTool: boolean = false;
  measureTool: { values: number[], valueStrings: string[] } = {
    values: [],
    valueStrings: []
  };

  private bounds = new mapboxgl.LngLatBounds();
  private _firstLoad = true;
  private markers: any = {};
  private markersOnScreen: any = {};
  private wellboresOnScreen: any[] = [];
  @ViewChild('mapContainer') mapContainer?: ElementRef;
  @ViewChild('markersContainer', { read: ViewContainerRef }) markersContainer?: ViewContainerRef;

  statusColors: string[] = ['#6e6e6e', '#04AC66', '#ff6600', '#ffae00', '#8197f2', '#6ec2f7'];
  private donutColors = [
    '#04AC66', // operating
    '#ff6600', // next 7
    '#ffae00', // next 8-30
    '#8197f2', // next 31–60
    '#6ec2f7'  // next 61–90
  ];

  showWeatherOverlay: boolean = false;
  weatherData: Map<string, WeatherForecast> = new Map();
  weatherLoading: boolean = false;
  weatherLayerAdded: boolean = false;
  selectedWeatherLayer: string = 'precipitation';
  weatherOpacity: number = 80;
  weatherUnavailable: boolean = false;
  showWeatherBetaPopup = false;
  private hasShownWeatherBetaNotice = false;
  private weatherBetaTimeoutId: any = null;

  // Single shared popup
  private sharedPopup?: any;
  private sharedPopupComponentRef?: ComponentRef<MarkerPopupComponent>;
  private sharedPopupComponent?: MarkerPopupComponent;
  private activePopupWellId?: string;
  // Id of the marker currently backing the open popup ("w_<wellId>" for a single
  // well, or the shared group marker id for wells stacked at the same location).
  private activePopupMarkerId?: string;
  private _isReopening = false;

  // Wells whose surface location rounds to the same point (e.g. multiple wells
  // drilled from one pad) are visually indistinguishable and would otherwise
  // stack their marker elements exactly on top of each other, so only the
  // topmost one could ever be clicked. Round to 5 decimal places (~1m) to group
  // them under a single marker instead.
  private static readonly GROUP_COORD_PRECISION = 5;

  // Drawer state
  drawerOpen = false;
  drawerWell?: FracScheduleModel;
  drawerWeather?: WeatherForecast;
  drawerTab: 'operations' | 'weather' = 'operations';
  drawerReview?: FracScheduleReviewModel;

  // Region state
  regionSidebarOpen = true;
  regionSummary: { region: string; count: number; activeCount: number; color: string }[] = [];

  weatherBetaMessage =
    'Weather Overlay (): Provided as an operational aid. Availability is not guaranteed and data may be temporarily unavailable. Verify critical decisions using primary weather sources.';


  weatherUnavailableMessage = 'Weather data is currently unavailable. Please confirm conditions using an alternate weather source.';
  enableMeasurement(enable: boolean) {
    this.showMeasureTool = enable;
    if (enable) this.startDistanceMeasure();
    else this.resetDistanceMeasure();
  }

  toggleWeatherOverlay() {
    const turningOn = !this.showWeatherOverlay;
    if (turningOn) this.showWeatherBetaNoticeOnce();

    this.showWeatherOverlay = !this.showWeatherOverlay;

    if (this.showWeatherOverlay) {
      if (this.weatherData.size === 0) {
        this.loadWeatherData();
      } else {
        this.updateMarkersWithWeather();
      }

      this.addWeatherLayer();
    } else {
      this.removeWeatherLayer();
      this.updateMarkersWithWeather();
    }
  }

  private showWeatherBetaNoticeOnce() {
    if (this.hasShownWeatherBetaNotice) return;

    this.hasShownWeatherBetaNotice = true;

    // Ensure we don't stack timers / state
    if (this.weatherBetaTimeoutId) {
      clearTimeout(this.weatherBetaTimeoutId);
      this.weatherBetaTimeoutId = null;
    }

    this.showWeatherBetaPopup = true;

    this.weatherBetaTimeoutId = setTimeout(() => {
      this.showWeatherBetaPopup = false;
      this.weatherBetaTimeoutId = null;
    }, 15000);
  }

  private loadWeatherData() {
    if (!this.data || this.data.length === 0) {
      console.warn('❌ No well data available');
      return;
    }

    this.weatherLoading = true;

    // Extract unique well locations
    const wellLocations = this.data
      .filter(w => w.surfaceLat && w.surfaceLong && w.wellId)
      .map(w => ({
        wellId: w.wellId,
        lat: w.surfaceLat!,
        lon: w.surfaceLong!
      }));

    // Fetch weather for all wells
    this.weatherService.getWeatherForWells(wellLocations).subscribe({
      next: (forecasts) => {
        const unavailable = forecasts.find(f => f.unavailable);

        if (unavailable) {
          this.weatherUnavailable = true;
          this.weatherUnavailableMessage =
            unavailable.unavailableMessage ??
            'Weather data is currently unavailable. Please confirm conditions using an alternate weather source.';
        } else {
          this.weatherUnavailable = false;
          // keep default message; no need to blank it
        }



        // Store weather data by wellId
        forecasts.forEach(forecast => {
          this.weatherData.set(forecast.wellId, forecast);
        });

        // Update markers with weather icons
        this.updateMarkersWithWeather();
        this.weatherLoading = false;
      },
      error: (err) => {
        this.weatherLoading = false;
      }
    });
  }

  changeWeatherLayer() {
    if (!this.map) return;

    const layers = ['precipitation', 'clouds', 'temperature', 'wind'];

    layers.forEach(layer => {
      const visibility = layer === this.selectedWeatherLayer ? 'visible' : 'none';
      this.map!.setLayoutProperty(`weather-${layer}`, 'visibility', visibility);
    });

    // Toggle temp badges on markers
    this.updateTempBadgeVisibility();
  }

  private updateTempBadgeVisibility() {
    const show = this.showWeatherOverlay && this.selectedWeatherLayer === 'temperature';
    document.querySelectorAll('.temp-badge').forEach((badge: any) => {
      badge.style.display = show ? 'block' : 'none';
    });
  }

  updateWeatherOpacity() {
    if (!this.map) return;

    const layers = ['precipitation', 'clouds', 'temperature', 'wind'];
    const opacity = this.weatherOpacity / 100;

    layers.forEach(layer => {
      if (this.map!.getLayer(`weather-${layer}`)) {
        this.map!.setPaintProperty(`weather-${layer}`, 'raster-opacity', opacity);
      }
    });
  }
  private addWeatherLayer() {
    if (!this.map || this.weatherLayerAdded) {
      return;
    }

    try {
      const tileUrl = (layer: string) =>
        `${environment.apiConfig.uri}api/weather/tile/${layer}/{z}/{x}/{y}`;

      // 1. PRECIPITATION LAYER (rain/snow) - MORE VISIBLE
      this.map.addLayer({
        id: 'weather-precipitation',
        type: 'raster',
        source: {
          type: 'raster',
          tiles: [tileUrl('precipitation_new')],
          tileSize: 256
        },
        paint: {
          'raster-opacity': 0.8  // ✅ Increased from 0.6 for better visibility
        }
      }, 'wells-outer-circle');

      // 2. CLOUDS LAYER - Shows cloud coverage
      this.map.addLayer({
        id: 'weather-clouds',
        type: 'raster',
        source: {
          type: 'raster',
          tiles: [tileUrl('clouds_new')],
          tileSize: 256
        },
        paint: {
          'raster-opacity': 0.5
        }
      }, 'weather-precipitation');

      // 3. TEMPERATURE LAYER - Color-coded temps (optional)
      this.map.addLayer({
        id: 'weather-temperature',
        type: 'raster',
        source: {
          type: 'raster',
          tiles: [tileUrl('temp_new')],
          tileSize: 256
        },
        paint: {
          'raster-opacity': 0.4
        },
        layout: {
          'visibility': 'none'  // Hidden by default, can toggle
        }
      }, 'weather-clouds');

      this.map.addLayer({
        id: 'weather-wind',
        type: 'raster',
        source: {
          type: 'raster',
          tiles: [tileUrl('wind_new')],
          tileSize: 256
        },
        paint: { 'raster-opacity': this.weatherOpacity / 100 },
        layout: {
          visibility: 'none'
        }
      }, 'weather-temperature');

      this.weatherLayerAdded = true;
    } catch (error) {
      // weather layers failed to add — map may not be ready
    }
  }

  private removeWeatherLayer() {
    if (!this.map || !this.weatherLayerAdded) return;

    const layers = ['weather-precipitation', 'weather-clouds', 'weather-temperature', 'weather-wind'];

    layers.forEach(layerId => {
      if (this.map!.getLayer(layerId)) {
        this.map!.removeLayer(layerId);
      }
      if (this.map!.getSource(layerId)) {
        this.map!.removeSource(layerId);
      }
    });

    this.weatherLayerAdded = false;
  }
  private updateMarkersWithWeather() {
    if (!this.map) return;

    // If the popup is currently open, update its weather data
    if (this.activePopupWellId && this.sharedPopupComponent) {
      if (this.showWeatherOverlay && this.weatherData.has(this.activePopupWellId)) {
        this.sharedPopupComponent.updateWeather(this.weatherData.get(this.activePopupWellId));
      } else {
        this.sharedPopupComponent.updateWeather(undefined);
      }
      this.cdr.detectChanges();
    }

    // Show/hide temp badges based on current state
    this.updateTempBadgeVisibility();
  }

  map?: mapboxgl.Map;
  ngOnInit(): void {
    this.map = new mapboxgl.Map({
      accessToken: environment.mapbox.accessToken,
      container: 'mapContainer',
      style: environment.mapbox.style,
      zoom: environment.mapbox.defaultZoom,
      center: [environment.mapbox.defaultLng, environment.mapbox.defaultLat]
    });

    this.map.addControl(new mapboxgl.NavigationControl({
      showZoom: true,
      showCompass: false
    }), "bottom-right");

    this.map.getCanvas().style.width = "100%";
    this.setValueString();

    this.map.on('load', () => {
      if (!this.map) return;

      // SOURCE
      this.map.addSource('wells', {
        type: 'geojson',
        cluster: true,
        clusterMaxZoom: 9,
        clusterRadius: 60,
        clusterProperties: {
          operating: ['+', [
            'case',
            ['all',
              ['<', ['get', 'startin'], 0],
              ['>=', ['+', ['get', 'startin'], ['get', 'duration']], 0]
            ],
            1, 0
          ]],
          next7: ['+', ['case', ['all', ['>=', ['get', 'startin'], 1], ['<', ['get', 'startin'], 8]], 1, 0]],
          next30: ['+', ['case', ['all', ['>=', ['get', 'startin'], 8], ['<', ['get', 'startin'], 31]], 1, 0]],
          next60: ['+', ['case', ['all', ['>=', ['get', 'startin'], 31], ['<', ['get', 'startin'], 61]], 1, 0]],
          next90: ['+', ['case', ['>=', ['get', 'startin'], 61], 1, 0]]
        },
        data: {
          type: 'FeatureCollection',
          features: []
        }
      });

      this.map.addSource('wellbores', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: []
        }
      });

      // LAYERS
      this.map.addLayer({
        id: 'wells-outer-circle',
        source: 'wells',
        type: 'circle',
        paint: {
          'circle-color': [
            'case',
            ['all',
              ['<', ['get', 'startin'], 0],
              ['>=', ['+', ['get', 'startin'], ['get', 'duration']], 0]
            ],
            '#04AC66', // Operating
            ['<', ['get', 'startin'], 8],
            '#ff6600', // Next 7
            ['<', ['get', 'startin'], 31],
            '#ffae00', // Next 8-30
            ['<', ['get', 'startin'], 61],
            '#8197f2', // 31–60
            '#6ec2f7'  // 61–90
          ],
          'circle-opacity': 0.2,
          'circle-radius': 30
        },
        filter: ["all", ["==", "$type", "Point"], ["!has", "point_count"]]
      });

      this.map.addLayer({
        id: 'wells-inner-circle',
        source: 'wells',
        type: 'circle',
        paint: {
          'circle-color': [
            'case',
            ['all',
              ['<', ['get', 'startin'], 0],
              ['>=', ['+', ['get', 'startin'], ['get', 'duration']], 0]
            ],
            '#04AC66', // Operating
            ['<', ['get', 'startin'], 8],
            '#ff6600', // Next 7
            ['<', ['get', 'startin'], 31],
            '#ffae00', // Next 8-30
            ['<', ['get', 'startin'], 61],
            '#8197f2', // 31–60
            '#6ec2f7'  // 61–90
          ],
          'circle-radius': 10
        },
        filter: ["all", ["==", "$type", "Point"], ["!has", "point_count"]]
      });

      this.map.addLayer({
        id: 'wells-frac-lines',
        source: 'wellbores',
        type: 'line',
        layout: {
          "line-join": "round",
          "line-cap": "round"
        },
        paint: {
          'line-color': [
            'case',
            ['all',
              ['<', ['get', 'startin'], 0],
              ['>=', ['+', ['get', 'startin'], ['get', 'duration']], 0]
            ],
            '#04AC66', // Operating
            ['<', ['get', 'startin'], 8],
            '#ff6600', // Next 7
            ['<', ['get', 'startin'], 31],
            '#ffae00', // Next 8–30
            ['<', ['get', 'startin'], 61],
            '#8197f2', // 31–60
            '#6ec2f7'  // 61–90
          ],
          'line-width': 2
        },
        filter: ['==', '$type', 'LineString']
      });

      // Create the single shared popup component and Mapbox popup
      if (this.markersContainer) {
        this.sharedPopupComponentRef = this.markersContainer.createComponent(MarkerPopupComponent);
        this.sharedPopupComponent = this.sharedPopupComponentRef.instance;

        // Wire up popup "View Details" -> drawer
        this.sharedPopupComponent.viewDetails.subscribe((well: FracScheduleModel) => {
          this.sharedPopup?.remove();
          this.openDrawer(well);
        });

        // Wire up popup well picker (multiple wells at one location) -> keep
        // weather/state in sync with whichever well is now shown in the popup
        this.sharedPopupComponent.wellChanged.subscribe((well: FracScheduleModel) => {
          this.activePopupWellId = well.wellId;
          const weather = (this.showWeatherOverlay && this.weatherData.has(well.wellId))
            ? this.weatherData.get(well.wellId)
            : undefined;
          this.sharedPopupComponent?.updateWeather(weather);
          this.cdr.detectChanges();
        });

        // Returning to the group's picker list means no single well is active
        this.sharedPopupComponent.backToList.subscribe(() => {
          this.activePopupWellId = undefined;
          this.sharedPopupComponent?.updateWeather(undefined);
          this.cdr.detectChanges();
        });
      }
      this.sharedPopup = new mapboxgl.Popup({ closeButton: true, closeOnClick: false, maxWidth: '320px' });
      this.sharedPopup.setDOMContent(this.sharedPopupComponentRef!.location.nativeElement);
      this.sharedPopup.on('close', () => {
        if (this._isReopening) return;
        this.activePopupWellId = undefined;
        this.sharedPopupComponent?.clear();
      });

      this.updateMarkers(this.map);
      this.setUpSource(); // Load initial data
    });

    this.map.on('mousemove', (event: any) => {
      if (!this.map) return;
      const cursorLayer = this.map.getLayer('measure-points');
      this.map.getCanvas().style.cursor = cursorLayer
        ? (this.map.queryRenderedFeatures(event.point, { layers: ['measure-points'] }).length ? 'pointer' : 'crosshair')
        : 'grab';
    });

    // Close popup when clicking on the map (not on a marker)
    this.map.on('click', () => {
      if (this.sharedPopup && this.activePopupWellId) {
        this.sharedPopup.remove();
      }
    });

    this.map.on('data', (event: any) => {
      if (event.sourceId !== 'wells' || !event.isSourceLoaded) return;
      this.updateMarkers(this.map!);
    });

    this.map.on('move', () => {
      this.updateMarkers(this.map!);
    });

    this.map.on('moveend', () => {
      this.updateMarkers(this.map!);
    });

    // Close popup on user-initiated map movement
    this.map.on('movestart', (e: any) => {
      if (e.originalEvent && this.sharedPopup && this.activePopupWellId) {
        this.sharedPopup.remove();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['data'] && this.map && this.map.isStyleLoaded()) {
      this.setUpSource();
    }
  }

  setUpSource(hidePopup = false, updateBounds = true, data?: FracScheduleModel[]) {
    if (data) this.data = data;
    this.data.forEach(w => {
      if (typeof w.startIn !== 'number' || typeof w.duration !== 'number') {
        console.warn('Skipping malformed well:', w);
        return;
      }

      // Optional: You can coerce if needed, but only if values are present
      w.startIn = Number(w.startIn);
      w.duration = Number(w.duration);
    });

    if (this.map) {
      const source = this.map.getSource('wells');

      if (source) {
        //remove previous markers
        for (var id in this.markersOnScreen) {
          var marker = this.markersOnScreen[id];
          marker.remove();
        }
        Array.prototype.forEach.call(document.querySelectorAll('.map-marker'), node => {
          node.parentNode.removeChild(node);
        });
        // Don't call markersContainer.clear() — it would destroy the shared popup component
        this.markers = {};
        this.markersOnScreen = {};
        this.wellboresOnScreen = [];
        // Close the shared popup and drawer if open
        if (this.sharedPopup) {
          this.sharedPopup.remove();
        }
        this.activePopupWellId = undefined;
        this.drawerOpen = false;
        this.data.forEach(w => {
          w.startIn = Number(w.startIn);
          w.duration = Number(w.duration);
        });

        (source as any).setData({
          type: 'FeatureCollection',

          features: this.data
            .filter((w) => {
              const hasLocation = !!w.surfaceLat && !!w.surfaceLong;
              const startIn = Number(w.startIn ?? NaN);
              const duration = Number(w.duration ?? NaN);

              const isOperating = !isNaN(startIn) && !isNaN(duration) && startIn < 0 && (startIn + duration) >= 0;
              const isScheduledUpcoming = !isNaN(startIn) && startIn >= 0;

              const include = hasLocation && (isOperating || isScheduledUpcoming);

              return include;
            })
            .map((w: FracScheduleModel) => ({
              type: 'Feature',
              properties: {
                startin: Number(w.startIn ?? 0),
                duration: Number(w.duration ?? 0),
                wellId: w.wellId,
              },
              geometry: {
                type: 'Point',
                coordinates: [w.surfaceLong, w.surfaceLat],
              }
            }))

        });

        // Update region summary
        this.updateRegionSummary();

        if (this._firstLoad && this.data.length) {
          /* this.wells.forEach((w: any) => {
             this.bounds.extend([w.surfaceLong, w.surfaceLat]);
           });*/
          //this.resetFilterZoom();
          this._firstLoad = false;
        }

        if (this.data && this.data.length) {
          /*******************************************
           * 2019-09-05 - If we have wells, let's bound the box to the data set
           * #168141157
           * ********************************************/
          //Set bounds up
          //console.log("Set Bounds");


          if (updateBounds) {

            this.bounds = new mapboxgl.LngLatBounds();
            this.data.forEach((w: any) => {
              if (w.surfaceLat != null && w.surfaceLong != null) {
                this.bounds.extend([w.surfaceLong, w.surfaceLat]);
              }

            });
            if (!this.bounds.isEmpty()) {
              this.map.fitBounds(this.bounds, {
                padding: 180,
                maxZoom: 12.5,
                duration: 3000
              });
            }
          }

        }
      }
    }
  }

  cdr = inject(ChangeDetectorRef);

  private locationKey(lng: number, lat: number): string {
    return `${lng.toFixed(SchedulesMapComponent.GROUP_COORD_PRECISION)},${lat.toFixed(SchedulesMapComponent.GROUP_COORD_PRECISION)}`;
  }

  private groupMarkerId(wells: FracScheduleModel[]): string {
    return 'g_' + wells.map(w => w.wellId).sort().join('_');
  }

  private createGroupMarkerElement(wells: FracScheduleModel[]): HTMLElement {
    const el = document.createElement('div');
    el.className = 'map-marker map-marker-group';
    // "6×" rather than a bare count, so it isn't misread as a single well's day countdown
    el.innerText = `${wells.length}×`;
    el.title = `${wells.length} wells at this location`;
    return el;
  }

  updateMarkers(map: mapboxgl.Map) {
    if (!map || !this.markersContainer) return;

    // keep track of new markers
    var newMarkers: any = {};
    var visibleWellbores: { [index: string]: FracScheduleModel } = {};
    // get the features whether or not they are visible (https://docs.mapbox.com/mapbox-gl-js/api/#map#queryrenderedfeatures)
    const features = map.querySourceFeatures('wells');

    // Non-cluster wells are grouped by (rounded) surface location so that wells
    // stacked on the same pad share a single, clickable marker instead of each
    // getting their own marker element directly on top of the others.
    const locationGroups = new Map<string, FracScheduleModel[]>();

    // loop through each feature
    features.forEach((feature: any) => {
      const coordinates = feature.geometry.coordinates;
      // get our properties, which include our clustered properties
      const props = feature.properties;
      // continue only if the point is part of a cluster
      if (!props.cluster) {
        const well = this.data.find((w) => w.wellId == props.wellId);
        if (!well || !well.surfaceLat || !well.surfaceLong) return;

        if (well.bottomholeLong && well.bottomholeLat && (well.surfaceLong != well.bottomholeLong || well.surfaceLat != well.bottomholeLat)) {
          visibleWellbores[well.wellId] = well;
        }

        const key = this.locationKey(well.surfaceLong, well.surfaceLat);
        const group = locationGroups.get(key);
        if (group) {
          if (!group.some(w => w.wellId === well.wellId)) group.push(well);
        } else {
          locationGroups.set(key, [well]);
        }
      }
      else {
        const id = "c_" + props.cluster_id;
        var marker = this.markers[id];

        if (!marker) {
          var el = this.createDonutChart(id, props);
          marker = this.markers[id] = new mapboxgl.Marker({
            element: el
          }).setLngLat(coordinates);
        }
        newMarkers[id] = marker;

        // if the marker isn't already on screen then add it to the map
        if (!this.markersOnScreen[id]) {
          marker.addTo(map);
        }
      }

    });

    locationGroups.forEach((wells) => {
      if (wells.length === 1) {
        const well = wells[0];
        const id = "w_" + well.wellId;
        var marker = this.markers[id];
        if (!marker) {
          const el = document.createElement('div');

          if ((well.startIn ?? 0) > 0) {
            el.className = 'map-marker';
            el.innerText = well.startIn?.toString() ?? "S";
          } else {
            el.className = 'map-marker';
            el.innerText = well.startIn?.toString() ?? "Op.";
          }

          // Add temperature badge if weather data is available
          if (this.showWeatherOverlay && this.weatherData.has(well.wellId)) {
            const weather = this.weatherData.get(well.wellId)!;
            const tempBadge = document.createElement('div');
            tempBadge.className = 'temp-badge';
            tempBadge.innerText = `${Math.round(weather.current.temp)}°`;
            tempBadge.style.display = this.selectedWeatherLayer === 'temperature' ? 'block' : 'none';
            el.appendChild(tempBadge);
          }

          // Click handler looks up fresh well data at click time to avoid stale closures
          const wellId = well.wellId;
          el.addEventListener('click', (e: Event) => {
            e.stopPropagation();
            const currentWell = this.data.find(w => w.wellId === wellId);
            if (currentWell) {
              this.openPopupForWell(currentWell);
            }
          });

          marker = this.markers[id] = new mapboxgl.Marker(el).setLngLat([well.surfaceLong!, well.surfaceLat!]);
        }
        newMarkers[id] = marker;

        if (!this.markersOnScreen[id]) {
          marker.addTo(map);
        }
      } else {
        const id = this.groupMarkerId(wells);
        var marker = this.markers[id];
        if (!marker) {
          const el = this.createGroupMarkerElement(wells);

          // Click handler looks up fresh well data at click time to avoid stale closures
          const wellIds = wells.map(w => w.wellId);
          el.addEventListener('click', (e: Event) => {
            e.stopPropagation();
            const currentWells = wellIds
              .map(wid => this.data.find(w => w.wellId === wid))
              .filter((w): w is FracScheduleModel => !!w);
            if (currentWells.length) {
              this.openPopupForWellGroup(currentWells, id);
            }
          });

          marker = this.markers[id] = new mapboxgl.Marker(el).setLngLat([wells[0].surfaceLong!, wells[0].surfaceLat!]);
        }
        newMarkers[id] = marker;

        if (!this.markersOnScreen[id]) {
          marker.addTo(map);
        }
      }
    });

    // check if the marker with the cluster_id is already on the screen by iterating through our markersOnScreen object, which keeps track of that
    for (var id in this.markersOnScreen) {
      // if there isn't a new marker with that id, then it's not visible, therefore remove it.
      if (!newMarkers[id]) {
        var marker = this.markersOnScreen[id];
        marker.remove();
      }
    }
    this.markersOnScreen = newMarkers;

    // Close popup if its marker was removed (e.g. zoomed out to cluster)
    if (this.activePopupMarkerId && !this.markersOnScreen[this.activePopupMarkerId]) {
      this.sharedPopup?.remove();
    }

    this.updateWellbores(map, visibleWellbores);
  }

  private openPopupForWell(well: FracScheduleModel): void {
    if (!this.map || !this.sharedPopup || !this.sharedPopupComponent) return;
    if (!well.surfaceLong || !well.surfaceLat) return;

    // Remove popup first without clearing data (prevent close handler from wiping state)
    this._isReopening = true;
    this.sharedPopup.remove();
    this._isReopening = false;

    const weather = (this.showWeatherOverlay && this.weatherData.has(well.wellId))
      ? this.weatherData.get(well.wellId)
      : undefined;

    this.sharedPopupComponent.loadWell(well, weather);
    this.activePopupWellId = well.wellId;
    this.activePopupMarkerId = 'w_' + well.wellId;

    this.setupPopupPos(well, this.sharedPopup);
    this.sharedPopup
      .setLngLat([well.surfaceLong, well.surfaceLat])
      .addTo(this.map);

    // Auto-pan to ensure popup is fully visible
    this.map.easeTo({
      center: [well.surfaceLong, well.surfaceLat],
      padding: { top: 100, bottom: 100, left: 100, right: this.drawerOpen ? 480 : 100 },
      duration: 500
    });

    this.cdr.detectChanges();
  }

  // Wells whose surface coordinates round to the same point (e.g. multiple
  // wells on one pad) share a single marker; clicking it shows a picker list
  // of all of them rather than any one well's detail.
  private openPopupForWellGroup(wells: FracScheduleModel[], markerId: string): void {
    if (!this.map || !this.sharedPopup || !this.sharedPopupComponent) return;
    const anchor = wells[0];
    if (!anchor.surfaceLong || !anchor.surfaceLat) return;

    this._isReopening = true;
    this.sharedPopup.remove();
    this._isReopening = false;

    this.sharedPopupComponent.loadGroup(wells);
    this.activePopupWellId = undefined;
    this.activePopupMarkerId = markerId;

    this.setupPopupPos(anchor, this.sharedPopup);
    this.sharedPopup
      .setLngLat([anchor.surfaceLong, anchor.surfaceLat])
      .addTo(this.map);

    this.map.easeTo({
      center: [anchor.surfaceLong, anchor.surfaceLat],
      padding: { top: 100, bottom: 100, left: 100, right: this.drawerOpen ? 480 : 100 },
      duration: 500
    });

    this.cdr.detectChanges();
  }

  setupPopupPos(well: FracScheduleModel, popup: any) {
    if (!(this.map && well.surfaceLong && well.surfaceLat)) return;
    const mapRect = this.map.getContainer().getBoundingClientRect();
    let popupOffset: any = {
      'top-left': [20, -15]
    };
    let popupAnchor = 'top-left';
    const markerPos = this.map.project([well.surfaceLong, well.surfaceLat]);
    if (markerPos.x + 320 > mapRect.width && markerPos.y + 200 < mapRect.height) {
      popupAnchor = 'top-right';
      popupOffset = {
        'top-right': [-20, -15]
      };
    }
    if (markerPos.x + 320 > mapRect.width && markerPos.y + 200 > mapRect.height) {
      popupAnchor = 'bottom-right';
      popupOffset = {
        'bottom-right': [-20, -15]
      };
    }
    if (markerPos.x + 320 < mapRect.width && markerPos.y + 200 > mapRect.height) {
      popupAnchor = 'bottom-left';
      popupOffset = {
        'bottom-left': [20, -15]
      };
    }
    popup.options.anchor = popupAnchor;
    popup.options.offset = popupOffset;
  }

  updateWellbores(map: mapboxgl.Map, wellbores: { [index: string]: FracScheduleModel }) {
    var newWellbores = Object.keys(wellbores);
    if (newWellbores.length == 0) return;
    newWellbores.sort();
    var update = newWellbores.length != this.wellboresOnScreen.length;
    if (!update) {
      for (var i = 0; i < newWellbores.length; i++) {
        if (newWellbores[i] != this.wellboresOnScreen[i]) {
          update = true;
          break;
        }
      }
    }
    if (!update) return;

    const source = map.getSource('wellbores');
    if (source) {
      (source as any).setData({
        type: 'FeatureCollection',
        features: newWellbores.map<any>((id: any) => {
          var w = wellbores[id];
          return {
            type: "Feature",
            properties: {
              startin: w.startIn
            },
            geometry: {
              type: 'LineString',
              coordinates: [
                [w.surfaceLong, w.surfaceLat],
                [w.bottomholeLong, w.bottomholeLat]
              ]
            }
          };
        })
      });
      this.wellboresOnScreen = newWellbores;
    }
  }

  // GeoJSON object to hold our measurement features
  selectedPoints: any = {
    'type': 'FeatureCollection',
    'features': []
  };

  // Used to draw a line between points
  measureLines: any = {
    'type': 'Feature',
    'geometry': {
      'type': 'LineString',
      'coordinates': []
    }
  };

  startDistanceMeasure() {
    if (!this.map) return;
    this.measureTool.values = [];
    // reset map
    this.resetDistanceMeasure();

    // add selected points source in map
    this.map.addSource('selected-points', {
      'type': 'geojson',
      'data': this.selectedPoints
    });

    // Add point layer to the map
    this.map.addLayer({
      id: 'measure-points',
      type: 'circle',
      source: 'selected-points',
      paint: {
        'circle-radius': 5,
        'circle-color': '#006bd5'
      },
      filter: ['in', '$type', 'Point']
    });

    // add layer to draw lines between points
    this.map.addLayer({
      id: 'measure-lines',
      type: 'line',
      source: 'selected-points',
      layout: {
        'line-cap': 'round',
        'line-join': 'round'
      },
      paint: {
        'line-color': '#006bd5',
        'line-width': 2.5
      },
      filter: ['in', '$type', 'LineString']
    });

    // bind click handler on map to set the selected points
    this.map.on('click', this.measureClickHandler);
    this.setValueString();
  }

  /**
   * handler on map to set the selected points
   */
  measureClickHandler = (e: any) => {
    if (!this.map || !this.measureTool) return;
    const features = this.map.queryRenderedFeatures(e.point, { layers: ['measure-points'] });

    // Remove the line string from the group
    // So we can redraw it based on the points collection
    if (this.selectedPoints.features.length > 1) { this.selectedPoints.features.pop(); }

    // Clear the Distance container to populate it with a new value
    this.measureTool.values = [];

    // If a feature was clicked, remove it from the map
    if (features.length && features[0]?.properties) {
      const id = features[0].properties['id'];
      this.selectedPoints.features = this.selectedPoints.features.filter(function (point: any) {
        return point.properties.id !== id;
      });
    } else {
      const point = {
        'type': 'Feature',
        'geometry': {
          'type': 'Point',
          'coordinates': [
            e.lngLat.lng,
            e.lngLat.lat
          ]
        },
        'properties': {
          'id': String(new Date().getTime())
        }
      };

      this.selectedPoints.features.push(point);
    }

    if (this.selectedPoints.features.length > 1) {
      this.measureLines.geometry.coordinates = this.selectedPoints.features.map(function (point: any) {
        return point.geometry.coordinates;
      });

      this.selectedPoints.features.push(this.measureLines);

      for (var i = 0; i < this.measureLines.geometry.coordinates.length - 1; i++) {
        var from = point(this.measureLines.geometry.coordinates[i]);
        var to = point(this.measureLines.geometry.coordinates[i + 1]);
        var dist = distance(from, to);
        this.measureTool.values[i] = dist * 1000;
      }
      // Populate the distanceContainer with total distance
      //const value = turf.lineDistance(this.measureLines) * 1000;
      //this.measureTool['value'] = value;
    }

    var src = this.map.getSource('selected-points');
    if (src) (src as any).setData(this.selectedPoints);
    e.originalEvent.stopPropagation();
    this.setValueString();
  }

  onSelectUnit() {
    this.setValueString();
  }

  setValueString() {

    this.measureTool.valueStrings = [];

    if (this.measureTool.values.length === 0) {
      if (this.selectedPoints.features.length > 0)
        this.measureTool.valueStrings[0] = "Click End point to measure";
      else
        this.measureTool.valueStrings[0] = "Click Start point to measure";
    }
    else {
      for (var i = 0; i < this.measureTool.values.length; i++) {
        let unitString: string = "m";
        let converted: number;
        if (this.selectedMeasureUnits === 'Miles') {
          converted = this.measureTool.values[i] * 0.0006213712;
          unitString = "miles";
        }
        else if (this.selectedMeasureUnits === 'Kilometers') {
          converted = this.measureTool.values[i] * 0.001;
          unitString = "km";
        }
        else if (this.selectedMeasureUnits === 'Feet') {
          converted = this.measureTool.values[i] * 3.28084;
          unitString = "ft";
        }
        else if (this.selectedMeasureUnits === 'Yards') {
          converted = this.measureTool.values[i] * 1.093613;
          unitString = "yd";
        }
        else {
          converted = this.measureTool.values[i];
          unitString = "m";
        }
        this.measureTool.valueStrings[i] = `${formatNumber(converted, 'en-US', '1.2-2')} ${unitString}`;
      }
    }
  }

  createDonutChart(id: string, props: any): HTMLElement {
    var offsets = [];
    var counts = [
      props.operating,
      props.next7,
      props.next30,
      props.next60,
      props.next90
    ];

    var total = 0;
    for (var i = 0; i < counts.length; i++) {
      offsets.push(total);
      total += counts[i];
    }
    var fontSize = total >= 1000 ? 22 : total >= 100 ? 20 : total >= 10 ? 18 : 16;
    var r = total >= 1000 ? 50 : total >= 100 ? 32 : total >= 10 ? 24 : 18;
    var r0 = Math.round(r * 0.6);
    var w = r * 2;

    var html = '<svg id="' + id + '" width="' + w + '" height="' + w + '" viewbox="0 0 ' + w + ' ' + w + '" text-anchor="middle" style="font: ' + fontSize + 'px sans-serif">';
    for (i = 0; i < counts.length; i++) {
      html += this.donutSegment(
        offsets[i] / total,
        (offsets[i] + counts[i]) / total,
        r,
        r0,
        this.donutColors[i]
      );
    }
    html += '<circle cx="' + r + '" cy="' + r + '" r="' + r0 + '" fill="white" />';
    html += '<text dominant-baseline="central" transform="translate(' + r + ', ' + r + ')">' + total.toLocaleString() + '</text>';
    html += '<circle class="cluster_mask" cx="' + r + '" cy="' + r + '" r="' + r + '" fill="rgba(255, 255, 255, .6)" style="visibility:hidden;" /></svg>';

    var el = document.createElement('div');
    el.innerHTML = html;
    return el.firstElementChild as HTMLElement;
  }

  donutSegment(start: number, end: number, r: number, r0: number, color: string) {
    if (end - start === 1) end -= 0.00001;
    var a0 = 2 * Math.PI * (start - 0.25);
    var a1 = 2 * Math.PI * (end - 0.25);
    var x0 = Math.cos(a0), y0 = Math.sin(a0);
    var x1 = Math.cos(a1), y1 = Math.sin(a1);
    var largeArc = end - start > 0.5 ? 1 : 0;

    return [
      '<path d="M',
      r + r0 * x0,
      r + r0 * y0,
      'L',
      r + r * x0,
      r + r * y0,
      'A',
      r,
      r,
      0,
      largeArc,
      1,
      r + r * x1,
      r + r * y1,
      'L',
      r + r0 * x1,
      r + r0 * y1,
      'A',
      r0,
      r0,
      0,
      largeArc,
      0,
      r + r0 * x0,
      r + r0 * y0,
      '" fill="' + color + '" />'
    ].join(' ');
  }

  /**
   * stop the measurement state
   */
  stopMeasure() {
    this.resetDistanceMeasure();
  }

  /**
   * reset the map due to changes by measurement tool
   */
  resetDistanceMeasure() {
    if (!this.map) return;
    if (this.map.getLayer('measure-lines')) {
      this.map.removeLayer('measure-lines');
    }
    if (this.map.getLayer('measure-points')) {
      this.map.removeLayer('measure-points');
    }
    if (this.map.getSource('selected-points')) {
      this.map.removeSource('selected-points');
    }
    this.map.off('click', this.measureClickHandler);
    this.measureLines.geometry.coordinates = [];
    this.selectedPoints.features = [];
    this.measureTool.valueStrings = [];
    this.measureTool.values = [];
    this.setValueString();
    //2019-09-05 - Dont reset zoom levels - https://www.pivotaltracker.com/story/show/168141059
    //this.map.setCenter([environment.mapbox.defaultLng, environment.mapbox.defaultLat]);
    //this.map.setZoom(environment.mapbox.defaultZoom);
  }

  styleSummaryLine(propName: string) {
    const sd = this.summaryData as any;

    const safe = (val: number | undefined) => (typeof val === 'number' && !isNaN(val)) ? val : 0;

    const tot = Math.max(
      safe(this.summaryData.operating),
      safe(this.summaryData.next7),
      safe(this.summaryData.next8_30),
      safe(this.summaryData.next31_60),
      safe(this.summaryData.next61_90)
    );

    const width = (tot === 0 ? 0 : (100 * safe(sd[propName]) / tot)) + '%';

    return {
      width,
      'padding-right': '50px'
    };
  }

  // ─── Drawer Methods ───

  openDrawer(well: FracScheduleModel): void {
    this.drawerWell = well;
    this.drawerWeather = (this.showWeatherOverlay && this.weatherData.has(well.wellId))
      ? this.weatherData.get(well.wellId)
      : undefined;
    this.drawerTab = 'operations';
    this.drawerReview = undefined;
    this.drawerOpen = true;

    // Fetch review data
    if (well.companyId && well.api) {
      this.http.get(`FracX/LastReviewByApi?companyId=${well.companyId}&api=${well.api}`, (review: FracScheduleReviewModel) => {
        if (review.reviewDate || review.reviewerName || review.riskLevel) {
          this.drawerReview = review;
        }
      }, false);
    }
  }

  closeDrawer(): void {
    this.drawerOpen = false;
  }

  getDrawerStatusText(): string {
    const well = this.drawerWell;
    if (!well) return '';
    if (well.startIn != null && well.duration != null) {
      if (well.startIn < 0 && -well.startIn >= well.duration) return 'Finished';
      if (well.startIn < 0) return 'Operating';
    }
    if (well.startIn === 0) return 'Started';
    if (well.startIn === 1) return 'Starting in 1 day';
    if (well.startIn && well.startIn > 1) return `Starting in ${well.startIn} days`;
    return 'Unknown';
  }

  getDrawerProgress(): number {
    const well = this.drawerWell;
    if (!well || well.startIn == null || well.startIn > 0) return 0;
    return Math.min(100, Math.max(0, (-well.startIn / (well.duration ?? 1)) * 100));
  }

  getDrawerProgressText(): string {
    const well = this.drawerWell;
    if (!well || well.startIn == null) return '';
    if (well.startIn > 0) return `Not Started - 0/${well.duration} days`;
    if (well.startIn <= 0) {
      const pct = ((-well.startIn) / (well.duration ?? 1) * 100).toFixed(0);
      return `${pct}% Completed - ${-well.startIn}/${well.duration} days`;
    }
    return '';
  }

  getDrawerRiskClass(riskLevel?: string): string {
    if (riskLevel === 'High') return 'risk-high';
    if (riskLevel === 'Medium') return 'risk-medium';
    if (riskLevel === 'Low') return 'risk-low';
    return '';
  }

  getWeatherIcon(icon: string): string {
    return `https://openweathermap.org/img/wn/${icon}@2x.png`;
  }

  getWeatherRiskLabel(risk: string): string {
    const labels: Record<string, string> = { low: 'Favorable', medium: 'Monitor', high: 'Challenging', critical: 'At Risk' };
    return labels[risk] || 'Unknown';
  }

  getWeatherRiskClass(risk: string): string {
    const classes: Record<string, string> = { low: 'risk-favorable', medium: 'risk-monitor', high: 'risk-challenging', critical: 'risk-challenging' };
    return classes[risk] || '';
  }

  // ─── Region Methods ───

  private readonly regionPalette = [
    '#04AC66', '#ff6600', '#ffae00', '#8197f2',
    '#6ec2f7', '#CE3D45', '#9664C8', '#64B464'
  ];

  private updateRegionSummary(): void {
    const regionGroups: Record<string, { total: number; active: number }> = {};

    this.data.forEach(w => {
      if (!w.region) return;
      if (!regionGroups[w.region]) regionGroups[w.region] = { total: 0, active: 0 };
      regionGroups[w.region].total++;

      const si = Number(w.startIn ?? 999);
      const dur = Number(w.duration ?? 0);
      if ((si < 0 && (si + dur) >= 0) || (si >= 0)) {
        regionGroups[w.region].active++;
      }
    });

    let colorIndex = 0;
    this.regionSummary = Object.entries(regionGroups)
      .map(([region, counts]) => ({
        region,
        count: counts.total,
        activeCount: counts.active,
        color: this.regionPalette[colorIndex++ % this.regionPalette.length]
      }))
      .sort((a, b) => b.activeCount - a.activeCount);
  }

  zoomToRegion(region: string): void {
    if (!this.map) return;

    const wells = this.data.filter(w => w.region === region && w.surfaceLat && w.surfaceLong);
    if (wells.length === 0) return;

    const bounds = new mapboxgl.LngLatBounds();
    wells.forEach(w => bounds.extend([w.surfaceLong!, w.surfaceLat!]));

    this.map.fitBounds(bounds, {
      padding: 80,
      maxZoom: 10,
      duration: 1500
    });
  }

  toggleRegionSidebar(): void {
    this.regionSidebarOpen = !this.regionSidebarOpen;
  }
}
